import React from "react";
import { ArrowLeft, FileText, ScrollText, Receipt } from "lucide-react";

/**
 * Pages légales — conformité e-commerce France/UE
 * - MentionsLegales : LCEN 2004-575 art. 6
 * - CGV : Code de la consommation art. L221-5 / L221-28
 * - PolitiqueRemboursement : exception droit de rétractation produits digitaux
 */

const LegalShell = ({ onBack, icon: Icon, title, subtitle, children }) => (
  <main data-testid="legal-page" className="min-h-screen bg-black text-white">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 lg:pt-36 lg:pb-24">
      <button
        data-testid="legal-back-btn"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-400 text-sm mb-8 transition-colors"
      >
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="flex items-center gap-3 mb-2">
        <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400">
          <Icon size={18} />
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">{title}</h1>
      </div>
      {subtitle && <p className="text-zinc-500 text-sm mb-10">{subtitle}</p>}

      <article className="legal-content space-y-4 text-zinc-300 leading-relaxed">
        {children}
      </article>

      <p className="text-zinc-600 text-xs mt-16 pt-6 border-t border-white/5">
        Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </div>
  </main>
);

export const MentionsLegales = ({ onBack }) => (
  <LegalShell
    onBack={onBack}
    icon={FileText}
    title="Mentions Légales"
    subtitle="Informations relatives à l'éditeur du site et à son hébergeur (LCEN n° 2004-575)"
  >
    <h2>Éditeur du site</h2>
    <p>
      Le site <strong>kazajahstudio.com</strong> est édité par <strong>KAZAJAH STUDIO / LABEL VERNABEL</strong>,
      label musical indépendant fondé par Didier Vernabel (DJHALLMANN) en 1995.
    </p>
    <ul>
      <li><strong>Directeur de la publication :</strong> Didier Vernabel (DJHALLMANN)</li>
      <li><strong>Adresse :</strong> La Cattedrale, Guadeloupe (FR) — Milan (IT)</li>
      <li><strong>Contact :</strong> <a href="mailto:contact@kazajahstudio.com">contact@kazajahstudio.com</a></li>
    </ul>

    <h2>Hébergement</h2>
    <p>
      Le site est hébergé par <strong>Emergent Agent</strong> (infrastructure Kubernetes managée).
      Pour toute requête technique relative à l'hébergement, écrire à <a href="mailto:contact@kazajahstudio.com">contact@kazajahstudio.com</a>.
    </p>

    <h2>Propriété intellectuelle</h2>
    <p>
      L'ensemble des éléments figurant sur le site (textes, images, vidéos, musiques, logos, identité visuelle,
      code source) est protégé par le droit d'auteur et le droit des marques. Toute reproduction,
      représentation, modification ou exploitation, totale ou partielle, sans autorisation écrite préalable est
      strictement interdite.
    </p>
    <p>
      Les œuvres musicales diffusées sur JAH RADIO, KAZAJAH TV et la Boutique restent la propriété exclusive de
      leurs auteurs et du LABEL VERNABEL.
    </p>

    <h2>Données personnelles</h2>
    <p>
      Conformément au RGPD (UE 2016/679) et à la loi Informatique et Libertés, vous disposez d'un droit d'accès,
      de rectification, d'effacement et d'opposition concernant les données vous concernant. Pour exercer ces
      droits : <a href="mailto:contact@kazajahstudio.com">contact@kazajahstudio.com</a>.
    </p>
    <p>
      Les données collectées (email lors d'un achat, coordonnées de paiement traitées par PayPal) sont
      strictement utilisées pour l'exécution de la commande et l'envoi du lien de téléchargement.
    </p>

    <h2>Cookies</h2>
    <p>
      Le site utilise uniquement des cookies techniques nécessaires au bon fonctionnement (session,
      authentification admin, lecteur audio). Aucun cookie tiers de profilage publicitaire n'est déposé.
    </p>

    <h2>Droit applicable</h2>
    <p>
      Le présent site et ses CGV sont soumis au droit français. Tout litige relève de la compétence des
      tribunaux français.
    </p>
  </LegalShell>
);

export const CGV = ({ onBack }) => (
  <LegalShell
    onBack={onBack}
    icon={ScrollText}
    title="Conditions Générales de Vente"
    subtitle="Vente de produits musicaux numériques (tracks MP3/WAV téléchargeables)"
  >
    <h2>Article 1 — Objet</h2>
    <p>
      Les présentes Conditions Générales de Vente (CGV) régissent la vente de produits numériques
      (fichiers musicaux MP3/WAV téléchargeables) proposés par <strong>KAZAJAH STUDIO / LABEL VERNABEL</strong>
      via le site <strong>kazajahstudio.com</strong>. Toute commande implique l'acceptation pleine et entière des présentes CGV.
    </p>

    <h2>Article 2 — Produits</h2>
    <p>
      Les produits sont des fichiers audio numériques. Chaque produit est décrit sur la page Boutique ou
      JAH RADIO : titre, artiste, genre, durée, prix TTC. Les visuels (pochettes) sont fournis à titre indicatif.
    </p>

    <h2>Article 3 — Prix</h2>
    <p>
      Les prix sont indiqués en euros (€) toutes taxes comprises. KAZAJAH STUDIO se réserve le droit de modifier
      ses prix à tout moment ; les produits seront facturés sur la base du tarif en vigueur au moment de la
      validation de la commande.
    </p>

    <h2>Article 4 — Commande</h2>
    <p>
      Pour passer commande, le client clique sur « Acheter », saisit son adresse email et procède au paiement
      via PayPal. Un email récapitulatif contenant le lien de téléchargement est envoyé automatiquement après
      confirmation du paiement.
    </p>

    <h2>Article 5 — Paiement</h2>
    <p>
      Le paiement s'effectue en ligne via <strong>PayPal</strong> (carte bancaire ou solde PayPal). KAZAJAH STUDIO
      n'a accès à aucune donnée bancaire ; l'intégralité du processus est sécurisée par PayPal (HTTPS / 3D Secure).
    </p>

    <h2>Article 6 — Livraison</h2>
    <p>
      La livraison est <strong>numérique et immédiate</strong> : un lien de téléchargement est envoyé à l'adresse
      email indiquée dès validation du paiement. Le lien reste actif pendant 7 jours. En cas de problème,
      contacter <a href="mailto:contact@kazajahstudio.com">contact@kazajahstudio.com</a>.
    </p>

    <h2>Article 7 — Droit de rétractation</h2>
    <p>
      Conformément à l'article <strong>L221-28 13°</strong> du Code de la consommation, le droit de rétractation
      ne peut être exercé pour les contrats de <strong>fourniture d'un contenu numérique non fourni sur un support matériel</strong>
      dont l'exécution a commencé après accord préalable exprès du consommateur et renoncement exprès à son droit de rétractation.
    </p>
    <p>
      En validant sa commande, le client <strong>renonce expressément à son droit de rétractation</strong> et accepte
      l'exécution immédiate (téléchargement) du contenu. Voir également la <strong>Politique de remboursement</strong>.
    </p>

    <h2>Article 8 — Garanties</h2>
    <p>
      KAZAJAH STUDIO garantit que les fichiers livrés sont conformes à la description et exempts de défauts
      techniques (corruption, fichier illisible). En cas de défaut avéré, un nouveau lien est fourni gratuitement.
    </p>

    <h2>Article 8 bis — Crédits AI Music</h2>
    <p>
      Les <strong>crédits AI Music</strong> permettent de générer des morceaux musicaux via le moteur d'intelligence
      artificielle intégré au site (KAZAJAH AI). Un crédit correspond à une génération d'une durée maximale de 60 secondes.
    </p>
    <ul>
      <li><strong>Validité</strong> : 12 mois à compter de la date d'achat. Au-delà, les crédits non utilisés expirent.</li>
      <li><strong>Non remboursables après usage</strong> : conformément à l'article L221-28 du Code de la consommation,
        l'exécution immédiate du service (génération musicale) prive le consommateur de son droit de rétractation.</li>
      <li><strong>Abonnement Illimité Mensuel</strong> : limité à 200 générations / mois (plafond technique pour éviter les abus).
        Renouvellement automatique tous les 30 jours via PayPal jusqu'à annulation. Annulable à tout moment depuis le compte PayPal.</li>
      <li><strong>Propriété intellectuelle</strong> : les morceaux générés par l'IA appartiennent à l'utilisateur,
        qui peut les exploiter commercialement. KAZAJAH STUDIO ne revendique aucun droit sur les compositions générées.</li>
    </ul>

    <h2>Article 9 — Propriété intellectuelle</h2>
    <p>
      L'achat d'une track confère un droit d'écoute privée. <strong>Toute revente, diffusion publique, sampling,
      remix ou exploitation commerciale est strictement interdite sans accord écrit préalable</strong> du label.
    </p>

    <h2>Article 10 — Service client</h2>
    <p>
      Toute réclamation peut être adressée à <a href="mailto:contact@kazajahstudio.com">contact@kazajahstudio.com</a>.
      Réponse sous 72 heures ouvrées.
    </p>

    <h2>Article 11 — Droit applicable</h2>
    <p>
      Les présentes CGV sont soumises au droit français. En cas de litige, une solution amiable sera recherchée
      avant toute action judiciaire. À défaut, les tribunaux français seront seuls compétents.
    </p>
  </LegalShell>
);

export const PolitiqueRemboursement = ({ onBack }) => (
  <LegalShell
    onBack={onBack}
    icon={Receipt}
    title="Politique de Remboursement"
    subtitle="Produits numériques téléchargeables — règles applicables"
  >
    <h2>Principe général</h2>
    <p>
      Les produits vendus sur <strong>kazajahstudio.com</strong> sont des contenus numériques (fichiers MP3/WAV) livrés
      immédiatement après paiement. Conformément à l'article <strong>L221-28 13°</strong> du Code de la consommation,
      le droit de rétractation de 14 jours ne s'applique pas dès lors que le téléchargement a commencé.
    </p>

    <h2>Cas où un remboursement est accordé</h2>
    <ul>
      <li><strong>Fichier corrompu ou illisible</strong> : nous fournissons d'abord un nouveau lien. Si le problème
        persiste, remboursement intégral sous 14 jours.</li>
      <li><strong>Double facturation</strong> : remboursement intégral sous 7 jours.</li>
      <li><strong>Produit manifestement non conforme</strong> à la description (mauvais titre/artiste) : remboursement intégral.</li>
      <li><strong>Paiement non suivi de livraison</strong> (lien jamais reçu malgré contact) : remboursement intégral.</li>
    </ul>

    <h2>Cas où un remboursement n'est pas accordé</h2>
    <ul>
      <li>Changement d'avis après téléchargement.</li>
      <li>Incompatibilité avec un logiciel ou un appareil (les formats MP3/WAV sont des standards universels).</li>
      <li>Track déjà téléchargée et utilisée.</li>
    </ul>

    <h2>Procédure</h2>
    <p>
      Envoyer un email à <a href="mailto:contact@kazajahstudio.com">contact@kazajahstudio.com</a> avec :
    </p>
    <ul>
      <li>L'adresse email utilisée lors de la commande</li>
      <li>L'ID de la transaction PayPal</li>
      <li>Le titre du track concerné</li>
      <li>La description du problème (et capture d'écran si possible)</li>
    </ul>
    <p>
      Nous répondons sous 72h ouvrées. Le remboursement, s'il est accordé, est effectué via PayPal sur le même
      moyen de paiement, sous 5 à 14 jours ouvrés.
    </p>

    <h2>Médiation</h2>
    <p>
      En cas de désaccord persistant, le consommateur peut saisir gratuitement le médiateur de la consommation
      ou la plateforme européenne de règlement en ligne des litiges&nbsp;:
      <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer"> ec.europa.eu/consumers/odr</a>.
    </p>
  </LegalShell>
);
